import React, { useMemo } from "react";
import ChatItemUsernameTooltip from "common/broadcast/broadcast-chat/chat-item-username-tooltip";
import { IChatMessage } from "common/broadcast/_stores/chat/interfaces";
import ChatStore from "common/broadcast/_stores/chat/ChatStore";
import NodeChatStore from "common/broadcast/_stores/nodechat/NodeChatStore";
import {
  TIER_BADGE_ICONS,
  TIER_BADGE_IMG_STYLE,
  TIER_BADGE_NAMES,
  getTierBadgeTier,
} from "common/broadcast/broadcast-chat/tier-badge";
import AiChatStore from "common/broadcast/_stores/ai-chat/AiChatStore";
import { inject, observer } from "mobx-react";
import { useIntl } from "react-intl";
import TailwindBox from "library/components/_tailwind/box";
import CustomIcon from "library/components/_tailwind/icon/icons";

type Props = {
  message: IChatMessage;
  chatStore?: ChatStore;
  aiChatStore?: AiChatStore;
  nodeChatStore?: NodeChatStore;
};

const ChatMessageText: React.ComponentType<Props> = ({
  message,
  chatStore,
  aiChatStore,
  nodeChatStore,
}) => {
  const { chatFontSize: fontSize } = chatStore!;
  const intl = useIntl();

  const isAiEnabled = aiChatStore!.isUiVisible;

  const isMemberMessage = !message.isModel && !message.isAdmin;

  const isSystemMessage = message.from === "System";

  const cleanUsername = useMemo(() => {
    if (!message.from) return "";
    if (isSystemMessage) {
      return intl.formatMessage({
        id: "broadcast.overlay.systemPrefix",
        defaultMessage: "[System]:",
      });
    }
    if (message.from.includes("ffadult") || message.from.includes("bdsm")) {
      return message.from.split(/_/)[0];
    } else {
      return message.from.split(/\+|@/)[0];
    }
  }, [message?.from, isSystemMessage, intl])

  const cleanTranslation = useMemo(() => {
    if(message?.text?.includes("(auto-translated)")) {
      return message.text.replace("(auto-translated)", "")
    }
    return message.text
  }, [message?.text])

  return (
    <div
      className={`ChatItem ${message.isAdmin ? "ChatItem--admin" : ""}`}
      style={{ fontSize }}>
      <TailwindBox className={["ChatItem__Username"]}>
        <ChatItemUsernameTooltip userId={message.userId} userName={cleanUsername}>
          <span
            data-testid={"chat-message-text"}
            className={`mr-2 ChatItem__from ${
              message.isModel ? "ChatItem__from--model" : ""
            }`}>
            {message.isAdmin &&
              intl.formatMessage({
                id: "broadcast.overlay.adminPrefix",
                defaultMessage: "[Admin] ",
              })}
            {message.isModel
              ? `${intl.formatMessage({
                  id: "broadcast.chatItem.me",
                  defaultMessage: "Me",
                })}:`
              : isSystemMessage
              ? cleanUsername
              : `${cleanUsername ? cleanUsername : message.from}:`}
            {(() => {
              const tier = getTierBadgeTier(
                nodeChatStore?.allMemberHash?.[message.userId ?? ""]?.fanTier
              );
              return tier ? (
                <img
                  src={TIER_BADGE_ICONS[tier]}
                  alt={TIER_BADGE_NAMES[tier]}
                  title={`${TIER_BADGE_NAMES[tier]} Tier Fan Club Member`}
                  style={{ ...TIER_BADGE_IMG_STYLE }}
                />
              ) : null;
            })()}
          </span>
        </ChatItemUsernameTooltip>
      </TailwindBox>
      {/* Do not refactor to TailwindText as we want the parent's font-size to take effect */}
      <TailwindBox
        as={"span"}
        style={{
          wordWrap: "break-word",
          color: message.isSentSuccessfully
            ? "var(--text-main-color)"
            : "#6b7280",
        }}>
        {cleanTranslation}
      </TailwindBox>
      {isAiEnabled && isMemberMessage && (
        <span
          style={{
            fontSize: "0.875rem",
            color: "var(--pink-color)",
            display: "inline-flex",
            verticalAlign: "middle",
            cursor: "pointer",
            marginLeft: "4px",
            opacity: 0.6,
          }}
          onClick={() => aiChatStore?.requestRecommendation(message)}>
          <CustomIcon name={"auto_awesome"} />
        </span>
      )}
    </div>
  );
};

export default inject(
  "chatStore",
  "aiChatStore",
  "nodeChatStore"
)(observer(ChatMessageText));
